/// <amd-module name="Search/WorkItem/Components/Pivot" />
import * as React from "react";
import * as Resources from "Search/WorkItem/Resources";
import * as Constants from "Search/WorkItem/Constants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService, INavigationStateChangedEvent } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssSearchService } from "VSS/Platform/Search";
import { getTFSPageData, ITfsPageData } from "Tfs/Platform/Page"
import { Filter } from "SearchUI/Utilities/Filter";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { ITabContext, IInputQuery } from "Search/Shared/Base/Types";
import { ResultsContainer } from "Search/WorkItem/Components/Results";
import { handlePivotNotification } from "Search/WorkItem/Components/Notification";
import { handlePivotCommands } from "Search/WorkItem/Components/PivotCommands";
import { ActionCreator } from "Search/WorkItem/Flux/ActionCreator";
import { ActionsHub } from "Search/WorkItem/Flux/ActionsHub";
import { TelemetryWriter } from "Search/WorkItem/Flux/Sources/TelemetryWriter";
import { TelemetrySpy } from "Search/WorkItem/Flux/Sources/TelemetrySpy";
import { PreferenceSpy } from "Search/WorkItem/Flux/Sources/PreferenceSpy";
import { CountSpy } from "Search/WorkItem/Flux/Sources/CountSpy";
import { WorkItemSearchSource } from "Search/WorkItem/Flux/Sources/WorkItemSearchSource";
import { StoresHub } from "Search/WorkItem/Flux/StoresHub";
import { applyNavigatedUrl, updateUrl } from "Search/WorkItem/NavigationHandler";
import { serializeFilters, getWindowTitle } from "Search/Shared/Utils";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { PivotStateServiceSpy } from "Search/WorkItem/Flux/Sources/PivotStateServiceSpy";
import { ISearchPivotStateService } from "Search/Shared/Base/PivotStateService";
import { EntityTypeUrlParam } from "Search/WorkItem/Constants";
import { BasePivot } from "Search/Shared/Components/BasePivot";
import { WorkItemSearchRequest } from "Search/LegacyWebApi/Contracts/Code";

export class WorkItemSearchPivot extends BasePivot {
    private readonly actionCreator: ActionCreator;
    private readonly storesHub: StoresHub;
    private readonly pageData: ITfsPageData | undefined;
    private readonly telemetryWriter: TelemetryWriter;
    private readonly telemetrySpy: TelemetrySpy;
    private readonly preferenceSpy: PreferenceSpy;
    private readonly countSpy: CountSpy;
    private readonly historyService: IVssHistoryService;
    private readonly includeFacets: boolean;
    private readonly useSeedDataSearchService: boolean;
    private readonly pivotStateServiceSpy: PivotStateServiceSpy;
    private readonly pivotStateService: ISearchPivotStateService;

    private disposeActions: (() => void)[];

    constructor(props: ITabContext, context: IVssComponentContext) {
        super(props, Constants.EntityTypeUrlParam, context);
        this.pageData = getTFSPageData(this.context.pageContext);
        const project = this.pageData && this.pageData.project;
        const filter = new Filter({ defaultState: {}, useApplyMode: false });
        const actionsHub: ActionsHub = new ActionsHub();
        this.preferenceSpy = new PreferenceSpy(this.context.pageContext, actionsHub);
        const isAssignedToFilterEnabled = props.isMember || isFeatureFlagEnabled(this.context.pageContext, "WebAccess.Search.WorkItem.EnableAssignedToFilterForPublicUsers");
        this.includeFacets = !isFeatureFlagEnabled(this.context.pageContext, Constants.FeatureFlagConstants.DisableAggregations, false);
        this.useSeedDataSearchService = isFeatureFlagEnabled(this.context.pageContext, "WebAccess.Search.WorkItem.EnableParallelSearchAndFPS", false);
        this.storesHub = new StoresHub(actionsHub, filter, isAssignedToFilterEnabled, this.includeFacets, props.isHosted, context.pageContext, props.isTabSwitch(), this.useSeedDataSearchService, project);
        this.telemetryWriter = new TelemetryWriter(props.isTabSwitch(), context.pageContext, props.telemetryMarker);
        
        this.actionCreator = new ActionCreator(
            this.context.pageContext,
            actionsHub,
            {
                searchSource: new WorkItemSearchSource(project, this.context.pageContext),
                pageSource: new PageSource(this.context.pageContext)
            },
            this.storesHub.getAggregatedState,
            filter,
            this.telemetryWriter,
            this.includeFacets,
            this.useSeedDataSearchService);
        this.telemetrySpy = new TelemetrySpy(this.telemetryWriter, actionsHub, this.storesHub);
        this.historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.countSpy = new CountSpy(props.resultsCount, actionsHub);
        this.pivotStateServiceSpy = new PivotStateServiceSpy(this.context.pageContext, actionsHub);
        this.pivotStateService = this.context.pageContext.getService<ISearchPivotStateService>("ISearchPivotStateService");
        this.disposeActions = [];
    }

    public render(): JSX.Element {
        return <ResultsContainer
            actionCreator={this.actionCreator}
            storesHub={this.storesHub}
            pageContext={this.context.pageContext}
            isHosted={this.props.isHosted}
            isMember={this.props.isMember}
        />;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.subscribe();
        const routeParams: { [key: string]: string } = this.historyService.getState();

        // Update search input placeholder and aria-label
        this.props.inputLabel.value = {
            inputPlaceholder: Resources.SearchWorkItemPlaceholder,
            inputAriaLabel: this.pageData && this.pageData.project ? Resources.WorkItemProjectInputAriaLabel : Resources.WorkItemOrgInputAriaLabel
        };

        const storedFilters = this.pivotStateService.getState(EntityTypeUrlParam);
        const filters = storedFilters && storedFilters.filtersString ? storedFilters.filtersString : this.getFilters();

        // init page.
        if (routeParams.text && routeParams.text.trim()) {
            if (!this.props.isTabSwitch() && this.useSeedDataSearchService && routeParams.udp === "false") {
                const includeFacets: boolean = routeParams.includeFacets ? JSON.parse(routeParams.includeFacets) : true;
                this.actionCreator
                    .loadInitialStateWithSeedData(
                        routeParams.text,
                        includeFacets,
                        routeParams.filters,
                        routeParams.sortOptions,
                        routeParams.action,
                        routeParams.lp,
                        routeParams.pageSize);
                const searchStore = this.storesHub.getAggregatedState();
                this.updatePageTitle(searchStore.searchStoreState.query);
            } else if (!this.props.isTabSwitch()) {
                const includeFacets: boolean = routeParams.includeFacets ? JSON.parse(routeParams.includeFacets) : true;
                this.actionCreator
                    .loadInitialState(
                        routeParams.text,
                        includeFacets,
                        routeParams.filters,
                        routeParams.sortOptions,
                        routeParams.action,
                        routeParams.lp,
                        routeParams.pageSize);
                const searchStore = this.storesHub.getAggregatedState();
                this.updatePageTitle(searchStore.searchStoreState.query);
            } else {
                this.actionCreator
                    .loadInitialState(
                        routeParams.text,
                        this.includeFacets,
                        filters,
                        undefined,
                        undefined,
                        undefined,
                        routeParams.pageSize);
            }
        } else {
            this.actionCreator.showLandingPage(this.getFilters());
        }

        // Update search provider for L1 search box
        const providerId = this.pageData && this.pageData.project
            ? "ms.vss-work-web.workitem-project-search-provider"
            : "ms.vss-work-web.workitem-collection-search-provider";
        this.context.pageContext.getService<IVssSearchService>("IVssSearchService").setSelectedProvider(providerId);

        // setting udp = "true" for URL sharing / refresh scenarios
        if (routeParams.udp) {
            delete routeParams.udp;
            this.historyService.replaceState(routeParams);
        }
    }

    public componentWillUnmount(): void {
        this.disposeActions.forEach(action => action());
        this.actionCreator.dispose();
        this.storesHub.dispose();
        this.telemetrySpy.dispose();
        this.telemetryWriter.dispose();
        this.preferenceSpy.dispose();
        this.countSpy.dispose();
        this.pivotStateServiceSpy.dispose();
        super.componentWillUnmount();
    }

    private getFilters(): string | undefined {
        return this.pageData && this.pageData.project
            ? serializeFilters({
                [Constants.FilterKeys.ProjectFiltersKey]: [this.pageData.project.name]
            }) : undefined;
    }

    private onSearchQueryUpdated = ({ text, openInNewTab }: IInputQuery): void => {
        if (!openInNewTab && text) {
            this.actionCreator.applySearchText(text);
        } else if (text) {
            this.actionCreator.openSearchInNewTab(text);
        }
    }

    private onNotificationStoreChanged = (): void => {
        handlePivotNotification(this.props.pageNotification, this.storesHub.getAggregatedState, this.actionCreator);
    }

    private onCommandsUpdated = (): void => {
        handlePivotCommands(this.props.pivotCommands, this.storesHub.getAggregatedState, this.actionCreator, this.props.isMember);
    }

    private subscribe = (): void => {
        const notificationStore = this.storesHub.getCompositeStore(["notificationStore"]);
        const commandsCompositeStore = this.storesHub.getCompositeStore(["sortOptionsStore", "previewOrientationStore", "filterStore"]);
        const searchStore = this.storesHub.getCompositeStore(["searchStore"]);
        const urlStore = this.storesHub.getCompositeStore(["searchStore", "sortOptionsStore"]);

        notificationStore.addChangedListener(this.onNotificationStoreChanged);
        commandsCompositeStore.addChangedListener(this.onCommandsUpdated);
        searchStore.addChangedListener(this.onSearchStoreUpdated);
        urlStore.addChangedListener(this.onUpdateUrl);

        // Subscribe to changes in input query
        this.props.inputQuery.subscribe(this.onSearchQueryUpdated);
        
        this.disposeActions.push(() => notificationStore.removeChangedListener(this.onNotificationStoreChanged));
        this.disposeActions.push(() => commandsCompositeStore.removeChangedListener(this.onCommandsUpdated));
        this.disposeActions.push(() => searchStore.removeChangedListener(this.onSearchStoreUpdated));
        this.disposeActions.push(() => urlStore.removeChangedListener(this.onUpdateUrl));
        this.disposeActions.push(() => this.props.inputQuery.unsubscribe(this.onSearchQueryUpdated));
        
        // Due to single pass render changes commandsCompositeStore doesn't get updated due to which commands aren't loaded.
        // Hence calling it explicitly once here.
        handlePivotCommands(this.props.pivotCommands, this.storesHub.getAggregatedState, this.actionCreator, this.props.isMember);
    }

    private onSearchStoreUpdated = (): void => {
        const { searchStoreState } = this.storesHub.getAggregatedState();
        const { query } = searchStoreState;
        this.updatePageTitle(query);
    }

    private updatePageTitle(query: WorkItemSearchRequest): void {
        // Update title of the page.
        if (query) {
            // defer operation to next tick 
            setTimeout(() => {
                const title = getWindowTitle(Resources.SearchWorkItemTitle, query.searchText);
                this.context
                    .pageContext
                    .getService<ITfsNavigationService>("ITfsNavigationService")
                    .setPageTitle(title);
            }, 0);
        }
    }

    private onUpdateUrl = (): void => {
        updateUrl(this.context.pageContext, this.storesHub.getAggregatedState(), this.includeFacets);
    }

    protected onNavigationOccurred = (value: INavigationStateChangedEvent): void => {
        // When clicked on any item in work item search result, a dialog is invoked which adds history entry with same state.
        // When dismissed, it removes that history state which triggers this 'onNavigate' method. 
        // Calling applyNavigatedUrl only if state has changed. This is required to avoid reloading results in above scenario.
        const oldState = value.oldState && value.oldState.state || {};
        const newState = value.newState && value.newState.state || {};
        const oldStateKeys = Object.keys(oldState);
        const newStateKeys = Object.keys(newState);
        let hasChanged = oldStateKeys.length !== newStateKeys.length;

        if (!hasChanged) {
            for (const key of oldStateKeys) {
                hasChanged = newState[key] !== oldState[key];
                if (hasChanged) {
                    break;
                }
            }
        }

        if (hasChanged) {
            applyNavigatedUrl(this.actionCreator, this.historyService.getState(), this.storesHub.getAggregatedState());
        }
    }
}

VssComponent.register("workItemSearchPivot", WorkItemSearchPivot);
